import emojiData, { type EmojiMartData } from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useMemo, useState, type ReactNode } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/language";

// Port of the reference emoji-picker.tsx: emoji-mart in a popover, with the
// picker chrome translated when the app runs in Arabic. theme="auto" follows
// the system scheme, matching the app's default theme behavior.

const data = emojiData as EmojiMartData;

export function EmojiPicker({
  onSelect,
  children,
}: {
  onSelect: (emoji: string) => void;
  children: ReactNode;
}) {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);

  const i18n = useMemo(() => {
    if (language !== "ar") return undefined;
    return {
      search: t("EmojiPicker:Search"),
      search_no_results_1: t("EmojiPicker:NoResults1"),
      search_no_results_2: t("EmojiPicker:NoResults2"),
      pick: t("EmojiPicker:Pick"),
      categories: {
        activity: t("EmojiPicker:Categories:Activity"),
        flags: t("EmojiPicker:Categories:Flags"),
        foods: t("EmojiPicker:Categories:Foods"),
        frequent: t("EmojiPicker:Categories:Frequent"),
        nature: t("EmojiPicker:Categories:Nature"),
        objects: t("EmojiPicker:Categories:Objects"),
        people: t("EmojiPicker:Categories:People"),
        places: t("EmojiPicker:Categories:Places"),
        search: t("EmojiPicker:Categories:Search"),
        symbols: t("EmojiPicker:Categories:Symbols"),
      },
    };
  }, [language, t]);

  const handleSelect = (emoji: { native: string }) => {
    onSelect(emoji.native);
    setOpen(false);
  };

  return (
    // NOT modal: Radix's modal scroll-lock can't see into emoji-mart's shadow
    // DOM, so it treats the picker's own list as background and blocks every
    // wheel/touch scroll inside it. Non-modal leaves scrolling native; the
    // stopPropagation handlers keep the chat behind from moving instead.
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={10}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        className="tw:w-auto tw:overflow-hidden tw:rounded-2xl tw:border tw:border-border tw:p-0 tw:shadow-lg"
      >
        <Picker
          data={data}
          onEmojiSelect={handleSelect}
          i18n={i18n}
          theme="auto"
          previewPosition="none"
          skinTonePosition="none"
          perLine={8}
          emojiButtonSize={36}
          emojiSize={22}
        />
      </PopoverContent>
    </Popover>
  );
}
